import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useBrand } from "../lib/brand";
import { Bike, Car, MapPin, ArrowLeft, ShieldCheck, KeyRound, Mail, FileCheck } from "lucide-react";
import { isNoldDomain } from "../lib/domain";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";

// Onboarding "Email-only" : email = identité unique. Flux unifié :
// email → (compte existant avec PIN ? → login PIN) sinon → code email → PIN permanent → profil.
export default function Auth() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const location = useLocation();
  const isInscription = location.pathname === "/inscription";
  const agentCode = params.get("agent") || "";
  const role = isInscription ? "driver" : (params.get("role") || "passenger");
  const { user, login, setToken } = useAuth();
  const { name: brandName } = useBrand();

  const [step, setStep] = useState("email-entry"); // email-entry | email-otp | pin-login | set-pin
  const [email, setEmail] = useState("");
  const [emailCode, setEmailCode] = useState("");
  const [emailDemoCode, setEmailDemoCode] = useState("");
  const [pin, setPin] = useState("");
  const [loading, setLoading] = useState(false);
  const [pending, setPending] = useState(null); // {token, user} awaiting PIN setup
  const [forgotMode, setForgotMode] = useState(false); // "PIN oublié" → récupération par email
  const [promo, setPromo] = useState((params.get("ref") || params.get("promo") || "").toUpperCase());
  const [conditionsAccepted, setConditionsAccepted] = useState(false);
  const [phone, setPhone] = useState("");
  const [phoneCode, setPhoneCode] = useState("");
  const [phoneDemoCode, setPhoneDemoCode] = useState("");

  const isDriver = role === "driver";
  const isRestaurateur = role === "restaurateur";
  const accent = "#D94F2B";
  const emailValid = /^\S+@\S+\.\S+$/.test(email.trim());
  // Sur Nold Go, l'espace « Chauffeur » regroupe Livreur (vélo) + Chauffeur VTC.
  // On adapte le titre pour la clarté marketing.
  const _isNold = typeof window !== "undefined" && /(^|\.)(noldgo|noldgo-test)\./i.test(window.location.hostname);
  const title = role === "admin" ? t("auth.adminTitle") : isRestaurateur ? t("auth.restaurateurTitle") : isDriver ? (_isNold ? "Espace Chauffeur & Livreur" : t("auth.driverTitle")) : t("auth.passengerTitle");

  // Préserve l'intent (ex. livraison repas) à travers la redirection auth → dashboard.
  const _intent = params.get("intent");
  const _dest = (u) => {
    // Priorité 1 — restaurateur : si l'utilisateur a le rôle restaurateur (dans `role`
    // OU dans `roles[]`), on redirige vers /restaurateur, y compris pour les comptes
    // multi-rôles (restaurateur + livreur → /restaurateur gagne).
    const roles = Array.isArray(u.roles) ? u.roles : [];
    const isResto = u.role === "restaurateur" || roles.includes("restaurateur");
    if (isResto) return "/restaurateur";
    // Livreur Nold Food (vélo ou livraison) → /livreur ; chauffeur VTC (voiture/moto) → /driver.
    if (u.role === "driver") {
      const vt = u.vehicle_type;
      const dest = (vt === "bike" || vt === "delivery") ? "/livreur" : "/driver";
      return `${dest}${_intent ? `?intent=${encodeURIComponent(_intent)}` : ""}`;
    }
    return `/${u.role}${_intent ? `?intent=${encodeURIComponent(_intent)}` : ""}`;
  };

  useEffect(() => {
    if (step === "set-pin") return; // ne pas rediriger pendant la création du PIN
    if (user && user.role) navigate(_dest(user), { replace: true });
  }, [user, navigate, step]); // eslint-disable-line

  useEffect(() => {
    if (params.get("kicked")) {
      sessionStorage.removeItem("bsg_kicked");
      toast.error("Vous avez été déconnecté : connexion depuis un autre appareil.");
    }
  }, [params]);

  // Google 1-clic — retour d'Emergent Auth via fragment #session_id=XXX
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  useEffect(() => {
    const hash = location.hash || "";
    if (!hash.includes("session_id=")) return;
    const sid = new URLSearchParams(hash.replace(/^#/, "")).get("session_id");
    if (!sid) return;
    // On retire le fragment immédiatement pour éviter un ré-échange
    try { window.history.replaceState(null, "", location.pathname + location.search); } catch {}
    (async () => {
      setLoading(true);
      try {
        const zone = isNoldDomain() ? "europe" : undefined;
        const { data } = await api.post("/auth/google/session", {
          session_id: sid,
          zone,
          role: isDriver ? "driver" : undefined,
          ref: promo || undefined,
          agent: agentCode || undefined,
        });
        login(data.token, data.user);
        toast.success("Bienvenue " + (data.user?.name || "") + " ✓");
        navigate(_dest(data.user), { replace: true });
      } catch (e) {
        toast.error(apiError(e) || "Connexion Google échouée");
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.hash]);

  const continueWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + location.pathname + location.search;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  // 1) Point d'entrée : on saisit l'email → on route vers PIN (compte connu) ou code email.
  const sendEmailCode = async () => {
    const r = await api.post("/auth/email/send-code", { email: email.trim() });
    setEmailDemoCode(r.data.demo_code || "");
    setEmailCode("");
    setStep("email-otp");
    if (r.data.demo_code) toast.success("Code email (démo) : " + r.data.demo_code);
    else toast.success("Code de vérification envoyé par email 📧");
  };

  const continueEmail = async () => {
    if (!emailValid) return toast.error("Email valide requis.");
    if (isDriver && !conditionsAccepted) return toast.error("Veuillez accepter les Conditions Partenaire.");
    setLoading(true);
    try {
      const r = await api.post("/auth/check-email", { email: email.trim() });
      setForgotMode(false);
      if (r.data.exists && r.data.has_pin) {
        setPin("");
        setStep("pin-login");
      } else {
        await sendEmailCode();
      }
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  // 2) Vérifie le code email puis connecte/crée le compte (device binding via token frais).
  const verifyAndComplete = async () => {
    if (!emailCode.trim()) return toast.error("Entrez le code reçu par email.");
    setLoading(true);
    try {
      await api.post("/auth/email/verify-code", { email: email.trim(), code: emailCode.trim() });
      const r = await api.post("/auth/email/complete", {
        email: email.trim(), role,
        ref: promo.trim() || undefined, agent: agentCode || undefined, reset: forgotMode,
      });
      if (r.data.needs_pin) {
        // Création du PIN permanent (ou reset via "PIN oublié"). On garde le token EN MÉMOIRE
        // seulement (pas de localStorage) tant que le PIN n'est pas posé, pour éviter d'entrer
        // dans l'app sans PIN si l'onglet est fermé pendant cette étape.
        setPending({ token: r.data.token, user: r.data.user });
        setPin("");
        setStep("set-pin");
        return;
      }
      login(r.data.token, r.data.user);
      toast.success("✓");
      navigate(_dest(r.data.user), { replace: true });
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  // 3) Connexion rapide par PIN (compte existant).
  const loginPin = async () => {
    if (pin.length !== 4) return toast.error(t("auth.pinPh"));
    setLoading(true);
    try {
      const r = await api.post("/auth/login-pin", { email: email.trim(), pin, role });
      login(r.data.token, r.data.user);
      toast.success("✓");
      navigate(_dest(r.data.user), { replace: true });
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  const forgotPin = async () => {
    setLoading(true);
    try {
      setForgotMode(true);
      await sendEmailCode();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  // Connexion chauffeur par téléphone + code SMS (email facultatif, auto {tel}@bentskin.go)
  const sendPhoneCode = async () => {
    if (!phone.trim()) return toast.error("Entrez votre numéro de téléphone.");
    if (isDriver && !conditionsAccepted) return toast.error("Veuillez accepter les Conditions Partenaire.");
    setLoading(true);
    try {
      const r = await api.post("/auth/phone/send-code", { phone: phone.trim(), country_code: "CM" });
      setPhoneDemoCode(r.data.demo_code || "");
      setPhoneCode("");
      setStep("phone-otp");
      if (r.data.demo_code) toast.success("Code (démo) : " + r.data.demo_code);
      else toast.success("Code envoyé par SMS 📲");
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };

  const verifyPhone = async () => {
    if (!phoneCode.trim()) return toast.error("Entrez le code reçu par SMS.");
    setLoading(true);
    try {
      const r = await api.post("/auth/phone/login", {
        phone: phone.trim(), country_code: "CM", code: phoneCode.trim(), role,
        ref: promo.trim() || undefined, agent: agentCode || undefined,
      });
      if (r.data.needs_pin) { setPending({ token: r.data.token, user: r.data.user }); setPin(""); setStep("set-pin"); return; }
      login(r.data.token, r.data.user);
      toast.success("✓");
      navigate(_dest(r.data.user), { replace: true });
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };

  // 4) Enregistre le PIN permanent puis entre dans l'app (profil si nouveau).
  const setPinSubmit = async () => {
    if (pin.length !== 4 || !/^\d{4}$/.test(pin)) return toast.error(t("auth.pinPh"));
    setLoading(true);
    try {
      // Le token n'est pas encore en localStorage → on l'envoie explicitement pour cet appel.
      await api.post("/auth/set-pin", { pin }, { headers: { Authorization: `Bearer ${pending.token}` } });
      toast.success("✓");
      login(pending.token, pending.user);
      navigate(_dest(pending.user), { replace: true });
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  const pinInput = (val, setter, testid, onEnter) => (
    <Input
      data-testid={testid}
      value={val}
      onChange={(e) => setter(e.target.value.replace(/\D/g, "").slice(0, 4))}
      onKeyDown={(e) => { if (e.key === "Enter" && onEnter) onEnter(); }}
      inputMode="numeric"
      placeholder="••••"
      maxLength={4}
      className="mt-1.5 h-14 rounded-xl border-2 text-center text-2xl tracking-[0.6em] font-bold"
    />
  );

  const subtitle =
    step === "email-entry" ? "Entrez votre email pour continuer — un seul compte par email."
    : step === "phone-entry" ? "Entrez votre numéro — vous recevrez un code par SMS."
    : step === "phone-otp" ? "Entrez le code reçu par SMS"
    : step === "email-otp" ? (forgotMode ? "Code de récupération envoyé par email" : "Vérifiez votre email pour continuer")
    : step === "pin-login" ? email
    : t("auth.setPinSub");

  return (
    <div className="min-h-screen bg-[#FDF8F0] flex flex-col">
      <div className="max-w-md w-full mx-auto px-5 py-6 flex-1 flex flex-col">
        <button data-testid="auth-back" onClick={() => navigate("/")} className="flex items-center gap-2 text-[#75807A] hover:text-[#1A1A1A] text-sm font-medium">
          <ArrowLeft className="w-4 h-4" /> {brandName}
        </button>

        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
          className="mt-10 bg-white rounded-3xl border border-[#E2DFD8] p-7 shadow-[0_8px_40px_rgba(0,0,0,0.05)]"
        >
          <div className="w-14 h-14 rounded-2xl flex items-center justify-center" style={{ background: accent }}>
            {step === "pin-login" || step === "set-pin" ? <KeyRound className="w-7 h-7 text-white" /> : isDriver ? (isNoldDomain() ? <Car className="w-7 h-7 text-white" /> : <Bike className="w-7 h-7 text-white" />) : <MapPin className="w-7 h-7 text-white" />}
          </div>
          <h1 className="mt-5 font-head font-black text-2xl tracking-tight text-[#1A1A1A]">
            {step === "pin-login" ? t("auth.pinTitle") : step === "set-pin" ? t("auth.setPinTitle") : title}
          </h1>
          <p className="mt-1 text-[#75807A] text-sm">{subtitle}</p>
          {isInscription && agentCode && (
            <div data-testid="inscription-agent-badge" className="mt-3 inline-flex items-center gap-2 rounded-full bg-[#F0EBE1] px-3 py-1 text-xs font-semibold text-[#1A1A1A]">
              <ShieldCheck className="w-3.5 h-3.5 text-[#D94F2B]" /> Parrainé par un agent
            </div>
          )}

          {/* ÉTAPE 1 — Email */}
          {step === "email-entry" && (
            <div className="mt-7 space-y-4" data-testid="auth-email-entry">
              {/* Pour les chauffeurs/livreurs : Conditions Partenaire en TÊTE (avant Google & email) */}
              {isDriver && (
                <div data-testid="partner-conditions" className="rounded-2xl border-2 border-[#E2DFD8] bg-[#FDF8F0] p-4">
                  <div className="flex items-center gap-2 text-[#1A1A1A] font-head font-bold text-sm">
                    <FileCheck className="w-4 h-4 text-[#D94F2B]" /> Conditions Partenaire {isNoldDomain() ? "Nold Go" : "BentSkin Go"}
                  </div>
                  <ul className="mt-3 space-y-2 text-xs text-[#3B423E] leading-relaxed">
                    <li className="flex gap-2"><span className="font-bold text-[#D94F2B]">1.</span> Je suis indépendant, pas salarié.</li>
                    {!isNoldDomain() && (
                      <li className="flex gap-2"><span className="font-bold text-[#D94F2B]">2.</span> 0% de commission pendant 3 mois, puis 20%.</li>
                    )}
                    <li className="flex gap-2"><span className="font-bold text-[#D94F2B]">3.</span> Je respecte le code de la route et j'ai une assurance.</li>
                    <li className="flex gap-2"><span className="font-bold text-[#D94F2B]">4.</span> {isNoldDomain() ? "Nold Go" : "BentSkin Go"} est uniquement une plateforme de mise en relation.</li>
                  </ul>
                  <label data-testid="partner-conditions-label" className="mt-3 flex items-start gap-3 cursor-pointer">
                    <input type="checkbox" data-testid="partner-conditions-checkbox" checked={conditionsAccepted} onChange={(e) => setConditionsAccepted(e.target.checked)} className="mt-0.5 w-5 h-5 accent-[#D94F2B] shrink-0" />
                    <span className="text-sm font-semibold text-[#1A1A1A]">J'accepte les Conditions Partenaire {isNoldDomain() ? "Nold Go" : "BentSkin Go"}</span>
                  </label>
                </div>
              )}
              {/* Google 1-clic — Nold Go (passenger + livreur vélo uniquement).
                  Restaurateur exclu (bouton masqué) — chauffeur VTC (voiture/moto) exclu
                  côté backend au moment de la session (403 si vehicle_type ∈ {car, moto}). */}
              {isNoldDomain() && !isRestaurateur && (
                <>
                  <button
                    type="button"
                    data-testid="auth-google-btn"
                    onClick={() => {
                      if (isDriver && !conditionsAccepted) {
                        toast.error("Veuillez d'abord accepter les Conditions Partenaire.");
                        return;
                      }
                      continueWithGoogle();
                    }}
                    disabled={loading || (isDriver && !conditionsAccepted)}
                    className="w-full h-14 rounded-xl border-2 border-[#E2DFD8] bg-white flex items-center justify-center gap-3 font-bold text-[#1A1A1A] hover:border-[#D94F2B] hover:shadow-md transition-all disabled:opacity-60 disabled:hover:border-[#E2DFD8]"
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="#FBBC05" d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.83z"/>
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/>
                    </svg>
                    Continuer avec Google
                  </button>
                  <div className="relative flex items-center justify-center py-2">
                    <div className="absolute inset-x-0 top-1/2 h-px bg-[#E2DFD8]" />
                    <span className="relative bg-white px-3 text-xs font-semibold text-[#75807A] uppercase tracking-wide">ou</span>
                  </div>
                </>
              )}
              <div>
                <label className="text-sm font-medium text-[#3B423E]">Email</label>
                <div className="relative mt-1.5">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#75807A]" />
                  <Input data-testid="auth-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") continueEmail(); }} placeholder="votre@email.com" className="h-14 rounded-xl border-2 pl-11" />
                </div>
                <p className="mt-1 text-xs text-[#75807A]">Votre profil et votre portefeuille sont liés à cet email.</p>
              </div>
              <Button data-testid="auth-send-otp" onClick={continueEmail} disabled={loading || !emailValid} className="w-full h-14 rounded-xl text-white font-bold text-lg hover:-translate-y-0.5 transition-transform" style={{ background: accent }}>
                {loading ? t("common.loading") : "Continuer avec mon email"}
              </Button>
              {isDriver && (
                <button data-testid="auth-switch-phone" onClick={() => setStep("phone-entry")} className="w-full text-sm text-[#D94F2B] font-semibold">📲 Ou connectez-vous par téléphone (SMS)</button>
              )}
            </div>
          )}

          {/* ÉTAPE — Téléphone (chauffeur) */}
          {step === "phone-entry" && (
            <div className="mt-7 space-y-4" data-testid="auth-phone-entry">
              <div>
                <label className="text-sm font-medium text-[#3B423E]">Numéro de téléphone</label>
                <Input data-testid="auth-phone-input" value={phone} onChange={(e) => setPhone(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") sendPhoneCode(); }} placeholder={isNoldDomain() ? "+32 4XX XX XX XX" : "+237 6XX XXX XXX"} className="mt-1.5 h-14 rounded-xl border-2" />
                <p className="mt-1 text-xs text-[#75807A]">Vous recevrez un code par SMS. L'email est facultatif — nous en créons un pour vous.</p>
              </div>
              {isDriver && (
                <label data-testid="phone-conditions-label" className="flex items-start gap-3 cursor-pointer rounded-2xl border-2 border-[#E2DFD8] bg-[#FDF8F0] p-4">
                  <input type="checkbox" data-testid="phone-conditions-checkbox" checked={conditionsAccepted} onChange={(e) => setConditionsAccepted(e.target.checked)} className="mt-0.5 w-5 h-5 accent-[#D94F2B] shrink-0" />
                  <span className="text-sm font-semibold text-[#1A1A1A]">{isNoldDomain() ? "J'accepte les Conditions Partenaire (indépendant, assurance)." : "J'accepte les Conditions Partenaire (0 % commission 3 mois, indépendant, assurance)."}</span>
                </label>
              )}
              <Button data-testid="auth-send-sms" onClick={sendPhoneCode} disabled={loading || !phone.trim()} className="w-full h-14 rounded-xl text-white font-bold text-lg" style={{ background: accent }}>
                {loading ? t("common.loading") : "Recevoir le code par SMS"}
              </Button>
              <button data-testid="auth-switch-email" onClick={() => setStep("email-entry")} className="w-full text-sm text-[#75807A] hover:text-[#1A1A1A]">Utiliser mon email plutôt</button>
            </div>
          )}

          {/* ÉTAPE — Code SMS (chauffeur) */}
          {step === "phone-otp" && (
            <div className="mt-7 space-y-4" data-testid="auth-phone-otp">
              {phoneDemoCode && (
                <div className="rounded-xl bg-[#F0EBE1] border border-[#E2DFD8] p-4 flex items-center justify-between">
                  <div><div className="text-xs text-[#75807A] uppercase tracking-wide">Code SMS (démo)</div><div className="font-head font-black text-2xl tracking-[0.3em] text-[#1A1A1A]" data-testid="phone-demo-code">{phoneDemoCode}</div></div>
                  <button data-testid="use-phone-demo-code" onClick={() => setPhoneCode(phoneDemoCode)} className="text-sm font-semibold text-[#D94F2B] underline">Utiliser</button>
                </div>
              )}
              <div>
                <label className="text-sm font-medium text-[#3B423E]">Code reçu par SMS</label>
                <Input data-testid="auth-phone-code-input" value={phoneCode} onChange={(e) => setPhoneCode(e.target.value.replace(/\D/g, "").slice(0, 6))} onKeyDown={(e) => { if (e.key === "Enter") verifyPhone(); }} inputMode="numeric" placeholder="••••••" maxLength={6} className="mt-1.5 h-14 rounded-xl border-2 text-center text-2xl tracking-[0.4em] font-bold" />
                <p className="mt-1 text-xs text-[#75807A]">Envoyé au <span className="font-semibold text-[#1A1A1A]">{phone}</span></p>
              </div>
              <Button data-testid="auth-verify-phone" onClick={verifyPhone} disabled={loading} className="w-full h-14 rounded-xl text-white font-bold text-lg" style={{ background: accent }}>
                {loading ? t("common.loading") : "Se connecter"}
              </Button>
              <div className="flex justify-between text-sm">
                <button data-testid="auth-phone-back" onClick={() => setStep("phone-entry")} className="text-[#75807A] hover:text-[#1A1A1A]">Retour</button>
                <button data-testid="auth-phone-resend" onClick={sendPhoneCode} className="text-[#D94F2B] font-medium">Renvoyer</button>
              </div>
            </div>
          )}

          {/* ÉTAPE 2 — Code email */}
          {step === "email-otp" && (
            <div className="mt-7 space-y-4" data-testid="auth-email-step">
              {emailDemoCode && (
                <div className="rounded-xl bg-[#F0EBE1] border border-[#E2DFD8] p-4 flex items-center justify-between">
                  <div>
                    <div className="text-xs text-[#75807A] uppercase tracking-wide">Code email (démo)</div>
                    <div className="font-head font-black text-2xl tracking-[0.3em] text-[#1A1A1A]" data-testid="email-demo-code">{emailDemoCode}</div>
                  </div>
                  <button data-testid="use-email-demo-code" onClick={() => setEmailCode(emailDemoCode)} className="text-sm font-semibold text-[#D94F2B] underline">Utiliser</button>
                </div>
              )}
              <div>
                <label className="text-sm font-medium text-[#3B423E]">Code reçu par email</label>
                <Input data-testid="auth-email-code-input" value={emailCode} onChange={(e) => setEmailCode(e.target.value.replace(/\D/g, "").slice(0, 4))} onKeyDown={(e) => { if (e.key === "Enter") verifyAndComplete(); }} inputMode="numeric" placeholder="••••" maxLength={4} className="mt-1.5 h-14 rounded-xl border-2 text-center text-2xl tracking-[0.5em] font-bold" />
                <p className="mt-1 text-xs text-[#75807A]">Envoyé à <span className="font-semibold text-[#1A1A1A]">{email}</span></p>
              </div>
              <Button data-testid="auth-verify-email" onClick={verifyAndComplete} disabled={loading} className="w-full h-14 rounded-xl text-white font-bold text-lg hover:-translate-y-0.5 transition-transform" style={{ background: accent }}>
                {loading ? t("common.loading") : "Vérifier et continuer"}
              </Button>
              <div className="flex justify-between text-sm">
                <button data-testid="auth-email-back" onClick={() => setStep("email-entry")} className="text-[#75807A] hover:text-[#1A1A1A]">{t("auth.back")}</button>
                <button data-testid="auth-email-resend" onClick={sendEmailCode} className="text-[#D94F2B] font-medium">{t("auth.resend")}</button>
              </div>
            </div>
          )}

          {/* ÉTAPE — Connexion PIN (compte existant) */}
          {step === "pin-login" && (
            <div className="mt-7 space-y-4">
              <div>
                <label className="text-sm font-medium text-[#3B423E]">{t("auth.pinLabel")}</label>
                {pinInput(pin, setPin, "auth-pin-input", loginPin)}
              </div>
              <Button data-testid="auth-pin-login" onClick={loginPin} disabled={loading} className="w-full h-14 rounded-xl text-white font-bold text-lg hover:-translate-y-0.5 transition-transform" style={{ background: accent }}>
                {loading ? t("common.loading") : t("auth.pinLogin")}
              </Button>
              <div className="flex justify-between text-sm">
                <button data-testid="auth-change-email" onClick={() => setStep("email-entry")} className="text-[#75807A] hover:text-[#1A1A1A]">{t("auth.back")}</button>
                <button data-testid="auth-forgot-pin" onClick={forgotPin} className="text-[#D94F2B] font-medium">{t("auth.forgotPin")}</button>
              </div>
            </div>
          )}

          {/* ÉTAPE — Création du PIN permanent */}
          {step === "set-pin" && (
            <div className="mt-7 space-y-4">
              <div>
                <label className="text-sm font-medium text-[#3B423E]">{t("auth.pinLabel")}</label>
                {pinInput(pin, setPin, "auth-set-pin-input", setPinSubmit)}
                <p className="mt-1 text-xs text-[#75807A]">Ce code vous servira pour vos prochaines connexions rapides.</p>
              </div>
              <Button data-testid="auth-set-pin" onClick={setPinSubmit} disabled={loading} className="w-full h-14 rounded-xl text-white font-bold text-lg hover:-translate-y-0.5 transition-transform" style={{ background: accent }}>
                {loading ? t("common.loading") : t("auth.createPin")}
              </Button>
            </div>
          )}

          <div className="mt-6 flex items-center gap-2 text-xs text-[#75807A]">
            <ShieldCheck className="w-4 h-4 text-[#D94F2B]" /> {t("safety.f2t")}
          </div>
        </motion.div>
      </div>
    </div>
  );
}
